import React, { useEffect, useState } from 'react';
import PaymentMethodModal from './PaymentMethodModal';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../services/apiHelpers';

/**
 * ConsultationPaymentModal - Modal for consultation payment
 */
const ConsultationPaymentModal = ({
  isOpen,
  onClose,
  appointment,
  patientName,
  clinicId,
  onPaymentComplete,
  onPaymentFailed,
}) => {
  const { auth } = useAuth();
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(appointment?.paymentStatus || 'PENDING');
  const [consultationFee, setConsultationFee] = useState(null);

  // Fetch consultation fee from appointment or fallback to revenue endpoint
  useEffect(() => {
    if (isOpen && appointment?.id) {
      const resolvedClinicId = clinicId || auth?.clinic?.id || (typeof window !== 'undefined' && window.__CLINIC_ID__);
      
      // Use appointment.consultationFee if available
      if (appointment.consultationFee != null && Number(appointment.consultationFee) > 0) {
        setConsultationFee(Number(appointment.consultationFee));
      } else {
        // Fallback: fetch from revenue endpoint
        fetch(buildApiUrl(`clinic/revenue-payment/appointment/${appointment.id}`), {
          headers: { 'X-Clinic-Id': resolvedClinicId }
        })
          .then(res => res.json())
          .then(data => {
            const fee = Number(data?.data?.consultationFee || appointment.consultationFee || 0);
            setConsultationFee(fee);
          })
          .catch(() => {
            setConsultationFee(Number(appointment.consultationFee || 0));
          });
      }
    }
  }, [isOpen, appointment, clinicId]);

  useEffect(() => {
    setPaymentStatus(appointment?.paymentStatus || 'PENDING');
    setError(null);
  }, [appointment]);

  if (!isOpen || !appointment) return null;

  const resolvedClinicId = clinicId || auth?.clinic?.id || (typeof window !== 'undefined' && window.__CLINIC_ID__);
  const amountDue = consultationFee != null ? consultationFee : 0;

  const handleConfirm = async (paymentDetails) => {
    if (!appointment?.id) {
      setError('Missing appointment information.');
      return;
    }
    if (!resolvedClinicId) {
      setError('Clinic ID missing.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const response = await fetch(buildApiUrl('clinic/revenue-payment/update-status'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Clinic-Id': resolvedClinicId,
        },
        body: JSON.stringify({
          appointmentId: appointment.id,
          paymentStatus: 'COLLECTED',
          paymentMethod: paymentDetails.paymentMethod,
          paymentDate: new Date().toISOString(),
          consultationFee: paymentDetails.paidAmount,
          notes: paymentDetails.notes,
        }),
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.message || 'Unable to collect payment.');
      }

      setPaymentStatus('COLLECTED');
      const paymentDate = payload.data?.paymentDate || new Date().toISOString();

      window.dispatchEvent(new CustomEvent('paymentCollected', {
        detail: {
          appointmentId: appointment.id,
          paymentMethod: paymentDetails.paymentMethod,
          paymentDate,
        },
      }));

      if (onPaymentComplete) {
        onPaymentComplete(payload.data || payload);
      }
    } catch (err) {
      const message = err?.message || 'Payment collection failed.';
      setError(message);
      if (onPaymentFailed) onPaymentFailed(err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div style={overlayStyles.overlay}>
      <div style={overlayStyles.modal}>
        <div style={overlayStyles.header}>
          <div>
            <div style={overlayStyles.title}>Consultation Payment</div>
            <div style={overlayStyles.subtitle}>
              {patientName || appointment.patientName || 'Patient information unavailable'}
            </div>
          </div>
          <button onClick={onClose} style={overlayStyles.closeButton}>&times;</button>
        </div>

        <div style={overlayStyles.summaryBox}>
          <div style={overlayStyles.summaryRow}>
            <span>Appointment</span>
            <strong>#{appointment.id}</strong>
          </div>
          <div style={overlayStyles.summaryRow}>
            <span>Amount due</span>
            <strong>₹{amountDue.toFixed(2)}</strong>
          </div>
          <div style={overlayStyles.summaryRow}>
            <span>Status</span>
            <strong>{paymentStatus === 'COLLECTED' ? 'Already paid' : 'Awaiting collection'}</strong>
          </div>
        </div>

        {paymentStatus === 'COLLECTED' ? (
          <div style={overlayStyles.paidMessage}>
            This appointment payment has already been collected.
          </div>
        ) : (
          <PaymentMethodModal
            consultationFeeAssigned={amountDue}
            isLoading={isProcessing}
            onCancel={onClose}
            onConfirm={handleConfirm}
            appointmentId={appointment.id}
            clinicId={resolvedClinicId}
          />
        )}

        {error && <div style={overlayStyles.errorBox}>{error}</div>}
      </div>
    </div>
  );
};

const overlayStyles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.55)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    zIndex: 2000,
  },
  modal: {
    width: 'min(560px, 100%)',
    background: '#fff',
    borderRadius: '22px',
    padding: '24px',
    boxShadow: '0 30px 80px rgba(15, 23, 42, 0.18)',
    border: '1px solid rgba(15, 23, 42, 0.08)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '18px',
    marginBottom: '20px',
  },
  title: {
    fontSize: '20px',
    fontWeight: 800,
    color: '#111827',
  },
  subtitle: {
    fontSize: '13px',
    color: '#475569',
    marginTop: '4px',
  },
  closeButton: {
    border: 'none',
    background: 'transparent',
    fontSize: '28px',
    lineHeight: 1,
    color: '#475569',
    cursor: 'pointer',
    padding: 0,
  },
  summaryBox: {
    borderRadius: '18px',
    border: '1px solid #e2e8f0',
    background: '#f8fafc',
    padding: '16px 18px',
    marginBottom: '20px',
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '13px',
    color: '#334155',
    marginBottom: '10px',
  },
  paidMessage: {
    marginBottom: '18px',
    padding: '14px 16px',
    borderRadius: '16px',
    background: '#ecfdf5',
    border: '1px solid rgba(16, 185, 129, 0.23)',
    color: '#166534',
    fontSize: '14px',
    lineHeight: 1.6,
  },
  errorBox: {
    marginTop: '16px',
    padding: '12px 14px',
    borderRadius: '14px',
    background: '#fef2f2',
    border: '1px solid rgba(239, 68, 68, 0.2)',
    color: '#b91c1c',
    fontSize: '13px',
  },
};

export default ConsultationPaymentModal;